import type { WarehouseInfo } from '@econtractor/types';
import { safeIntParse } from 'utils/helpers/safeParsing';
import vconst from 'utils/helpers/structs/vconst';
import { toNullableObjectId } from './itemLine';
import type { SupplierRequestDraftItem } from './types';

export interface ComingObjectOption {
    objectId: number | null;
    objectName: string;
    clientName: string;
    onlySale: number;
    objectStatus: number | null;
    remaining: number;
}

/** Як WarehouseSelect / blockObject: лише відкриті об'єкти можна проводити на склад об'єкта/продажу. */
export const isComingObjectOpen = (objectStatus?: number | null): boolean => {
    if (objectStatus == null || Number.isNaN(Number(objectStatus))) return true;
    const status = Number(objectStatus);
    return status === vconst.objectInWork || status === vconst.objectPanding;
};

export const getObjectStatusFromWarehouses = (
    objectId: number,
    warehouses?: WarehouseInfo[],
): number | null => {
    const warehouse = (warehouses || []).find(
        (wh) =>
            Number(wh.objectId) === objectId &&
            !Number(wh.reservObjectId) &&
            !Number(wh.customId),
    );
    return warehouse?.objectStatus == null ? null : Number(warehouse.objectStatus);
};

export type ComingDest = 'main' | 'object' | 'reserve';

/** Усі об'єкти заявки (без фільтра) */
export const ALL_COMING_OBJECTS_KEY = -1;

export const toComingObjectKey = (objectId?: number | null): number =>
    toNullableObjectId(objectId) || 0;

export const isAllComingObjects = (objectKey: number | null): boolean =>
    objectKey === ALL_COMING_OBJECTS_KEY;

export const itemMatchesComingObject = (
    item: { objectId?: number | null },
    objectKey: number,
): boolean => {
    if (isAllComingObjects(objectKey)) return true;
    return toComingObjectKey(item.objectId) === objectKey;
};

export const comingMatchesObjectFilter = (
    comingObjectId: number | null | undefined,
    objectKey: number,
): boolean => {
    if (isAllComingObjects(objectKey)) return true;
    return toComingObjectKey(comingObjectId) === objectKey;
};

export const buildComingObjectOptions = (
    items: SupplierRequestDraftItem[],
): ComingObjectOption[] => {
    const map = new Map<number, ComingObjectOption>();
    for (const item of items) {
        const objectId = toNullableObjectId(item.objectId);
        const key = objectId || 0;
        const remaining = Math.max(
            0,
            (safeIntParse(item.itemCount) || 0) - (safeIntParse(item.receivedCount) || 0),
        );
        const existing = map.get(key);
        if (existing) {
            existing.remaining += remaining;
            continue;
        }
        map.set(key, {
            objectId,
            objectName: objectId ? String(item.objectName || '') : '',
            clientName: objectId ? String(item.clientName || '') : '',
            onlySale: objectId ? Number(item.onlySale || 0) : 0,
            objectStatus:
                objectId && item.objectStatus != null ? Number(item.objectStatus) : null,
            remaining,
        });
    }

    return Array.from(map.values()).sort((a, b) => {
        if (!a.objectId && b.objectId) return 1;
        if (a.objectId && !b.objectId) return -1;
        const aTitle = a.objectName || a.clientName || '';
        const bTitle = b.objectName || b.clientName || '';
        return aTitle.localeCompare(bTitle);
    });
};

export const hasReserveWarehouse = (
    warehouses: WarehouseInfo[] | undefined,
    objectId: number,
): boolean => (warehouses || []).some((wh) => Number(wh.reservObjectId) === objectId);

export const buildComingDestOptions = (
    objectKey: number | null,
    warehouses: WarehouseInfo[] | undefined,
    objectStatus?: number | null,
): ComingDest[] => {
    const dests: ComingDest[] = ['main'];
    const objectId = objectKey != null && objectKey > 0 ? objectKey : 0;
    if (!objectId) return dests;
    const status =
        objectStatus != null ? objectStatus : getObjectStatusFromWarehouses(objectId, warehouses);
    if (!isComingObjectOpen(status)) return dests;
    dests.push('object');
    if (hasReserveWarehouse(warehouses, objectId)) dests.push('reserve');
    return dests;
};
